import { TKioskClockOut } from 'types/kiosk/TKioskClockOut';
import { TKioskBreak } from 'types/kiosk/TKioskClockOutData';
import { TTimeRegistration } from 'types/kiosk/TTimeRegistration';
import { localDateToYmd } from './kiosk-date';
import { parseUtcTimestamp } from './date-utils';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** True when value is a well-formed "HH:mm". */
export const isHHmm = (value: string): boolean => TIME_RE.test(value);

/** "HH:mm" → minutes since midnight, or null if malformed. */
const hhmmToMinutes = (value: string): number | null => {
  if (!TIME_RE.test(value)) return null;
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
};

/** Midnight-safe break length in minutes: a break crossing midnight stays positive. */
export const breakDurationMinutes = (start: string, end: string): number => {
  const s = hhmmToMinutes(start);
  const e = hhmmToMinutes(end);
  if (s === null || e === null) return 0;
  return (((e - s) % 1440) + 1440) % 1440;
};

/** Indices of breaks that overlap another break, compared by minutes-of-day (same-day breaks). */
export const getOverlappingIndices = (breaks: TKioskBreak[]): number[] => {
  const ranges = breaks.map((b, i) => ({ i, s: hhmmToMinutes(b.startTime), e: hhmmToMinutes(b.endTime) }));
  const overlapping = new Set<number>();
  for (let a = 0; a < ranges.length; a += 1) {
    for (let b = a + 1; b < ranges.length; b += 1) {
      const ra = ranges[a];
      const rb = ranges[b];
      const valid = ra.s !== null && ra.e !== null && rb.s !== null && rb.e !== null;
      // Overlap when each interval starts before the other ends.
      if (valid && ra.s < rb.e && rb.s < ra.e) {
        overlapping.add(ra.i);
        overlapping.add(rb.i);
      }
    }
  }
  return Array.from(overlapping).sort((x, y) => x - y);
};

/** Every break is a complete, well-formed HH:mm pair. */
export const areBreaksValid = (breaks: TKioskBreak[]): boolean =>
  breaks.every(b => TIME_RE.test(b.startTime) && TIME_RE.test(b.endTime));

/** Builds a local Date from a local calendar date + "HH:mm", or null if the time is malformed. */
const localDateTime = (date: Date, hhmm: string): Date | null => {
  const minutes = hhmmToMinutes(hhmm);
  if (minutes === null) return null;
  const d = new Date(date);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
};

export type TKioskWorkSummary = {
  totalWorkedMinutes: number;
  deviationMinutes: number | null;
};

type TWorkSummaryInput = {
  startTimeUtc: string;
  date: Date;
  endWork: string;
  breaks: TKioskBreak[];
  plannedTotalMinutes: number | null;
};

/**
 * Live preview of the work summary (the server figure is authoritative on success).
 * - totalWorked = gross span (registrationEnd − registrationStart) − Σ break minutes, clamped ≥ 0.
 * - deviation   = totalWorked − plannedTotalMinutes; null unless the registration is linked to planning.
 *   plannedTotalMinutes comes from the server (span minus planned break, overnight-safe) so this mirrors
 *   the To-Be-Processed calculation exactly. Positive = worked more than planned (overtime).
 */
export const computeWorkSummary = ({
  startTimeUtc,
  date,
  endWork,
  breaks,
  plannedTotalMinutes,
}: TWorkSummaryInput): TKioskWorkSummary => {
  const start = parseUtcTimestamp(startTimeUtc);
  const end = localDateTime(date, endWork);
  if (!end || Number.isNaN(start.getTime())) {
    return { totalWorkedMinutes: 0, deviationMinutes: null };
  }
  const grossMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
  const breakMinutes = breaks.reduce((sum, b) => sum + breakDurationMinutes(b.startTime, b.endTime), 0);
  const totalWorkedMinutes = Math.max(0, grossMinutes - breakMinutes);

  const deviationMinutes = plannedTotalMinutes === null ? null : totalWorkedMinutes - plannedTotalMinutes;
  return { totalWorkedMinutes, deviationMinutes };
};

/** Builds the POST /ClockOut body. No cost centre; comment always null. */
export const buildKioskClockOutPayload = (
  employeeId: number,
  registrationId: number,
  date: Date,
  endWork: string,
  breaks: TKioskBreak[],
  pinVerificationToken?: string | null,
): TKioskClockOut => ({
  employeeId,
  registrationId,
  date: localDateToYmd(date),
  endWork,
  breaks: breaks.map(b => ({ startTime: b.startTime, endTime: b.endTime })),
  comment: null,
  ...(pinVerificationToken ? { pinVerificationToken } : {}),
});

/** Server-authoritative worked minutes from the clock-out response (for the success banner).
 *  Prefers adjusted times, falls back to raw, minus total break minutes, clamped ≥ 0. */
export const extractWorkedMinutes = (reg: TTimeRegistration): number => {
  const startIso = reg.adjustedStartTime ?? reg.startTime;
  const endIso = reg.adjustedEndTime ?? reg.endTime;
  if (!startIso || !endIso) return 0;
  const start = parseUtcTimestamp(startIso);
  const end = parseUtcTimestamp(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const gross = Math.round((end.getTime() - start.getTime()) / 60000);
  return Math.max(0, gross - (reg.totalBreakMinutes ?? 0));
};

/** Minutes → "8h 15m". */
export const formatWorkedDuration = (minutes: number): string => {
  const safe = Math.max(0, Math.round(minutes));
  return `${Math.floor(safe / 60)}h ${safe % 60}m`;
};

/** Signed deviation → "+0h 15m" / "−0h 15m" (real minus sign). */
export const formatDeviation = (minutes: number): string => {
  const sign = minutes < 0 ? '−' : '+';
  const abs = Math.abs(Math.round(minutes));
  return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
};
